import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  deleteTourAliasFn,
  listTourAliasesFn,
  saveTourAliasFn,
} from "@/lib/tour-aliases.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin/tour-aliases")({
  head: () => ({
    meta: [
      { title: "Alias de tours · Administración" },
      {
        name: "description",
        content:
          "Gestiona los alias de los tours para que las importaciones de reservas externas reconozcan los nombres usados por cada canal.",
      },
      { property: "og:title", content: "Alias de tours · Administración" },
      {
        property: "og:description",
        content: "Alias para el reconocimiento automático de tours al importar reservas externas.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: TourAliasesPage,
});

function TourAliasesPage() {
  const list = useServerFn(listTourAliasesFn);
  const save = useServerFn(saveTourAliasFn);
  const remove = useServerFn(deleteTourAliasFn);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["tour-aliases"],
    queryFn: () => list({ data: undefined as never }),
  });

  const [tourId, setTourId] = useState("");
  const [alias, setAlias] = useState("");

  const saveMut = useMutation({
    mutationFn: (input: { id?: string | null; tourId: string; alias: string }) =>
      save({ data: input }),
    onSuccess: () => {
      toast.success("Alias guardado");
      setAlias("");
      qc.invalidateQueries({ queryKey: ["tour-aliases"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Alias eliminado");
      qc.invalidateQueries({ queryKey: ["tour-aliases"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const tours = data?.tours ?? [];
  const aliases = data?.aliases ?? [];

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-semibold">Alias de tours</h1>
        <p className="text-muted-foreground text-sm">
          Los alias permiten que el importador reconozca los nombres que usan las OTAs y los
          proveedores (mayúsculas, acentos o palabras extra no impiden la coincidencia).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nuevo alias</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span>Tour del catálogo</span>
            <select
              className="border-input bg-background h-9 min-w-64 rounded-md border px-2 text-sm"
              value={tourId}
              onChange={(e) => setTourId(e.target.value)}
            >
              <option value="">Selecciona un tour…</option>
              {tours.map((t: { id: string; title: string; city: string | null }) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                  {t.city ? ` · ${t.city}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span>Alias</span>
            <Input
              className="min-w-64"
              value={alias}
              placeholder="Cozumel Snorkeling"
              onChange={(e) => setAlias(e.target.value)}
            />
          </label>
          <Button
            disabled={!tourId || !alias.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate({ tourId, alias })}
          >
            Guardar alias
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alias registrados ({aliases.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Cargando…</p>
          ) : aliases.length === 0 ? (
            <p className="text-muted-foreground text-sm">Todavía no hay alias registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="py-2">Alias</th>
                    <th className="py-2">Tour</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {aliases.map(
                    (a: { id: string; alias: string; tour_id: string }) => (
                      <tr key={a.id} className="border-border/50 border-t">
                        <td className="py-2">
                          <Input
                            defaultValue={a.alias}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v && v !== a.alias)
                                saveMut.mutate({ id: a.id, tourId: a.tour_id, alias: v });
                            }}
                          />
                        </td>
                        <td className="py-2">
                          {tours.find((t: { id: string }) => t.id === a.tour_id)?.title ?? "—"}
                        </td>
                        <td className="py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => delMut.mutate(a.id)}
                            disabled={delMut.isPending}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
